/**
 * ForceReturnCentralFillOrdersModal (POM-5455)
 *
 * Thin wrapper over the shared CentralFillOrdersActionModal supplying the Force
 * Return copy + API. Force Return is an INTERNAL operational override — it does
 * NOT contact the external compounding pharmacy. The reason is required and is
 * recorded only in PerfectRx audit + order history (never sent externally).
 */
import PropTypes from 'prop-types';

import { forceReturnCentralFillOrdersApi } from 'api/centralFill';
import CentralFillOrdersActionModal from './CentralFillOrdersActionModal';

const ForceReturnCentralFillOrdersModal = (props) => {
  const { open, orders, onClose, onProcessed } = props;

  return (
    <CentralFillOrdersActionModal
      open={open}
      orders={orders}
      onClose={onClose}
      onProcessed={onProcessed}
      idPrefix='force-return'
      title='Route to Default'
      subtitle='Review the selected orders before routing them to the default PerfectRx workflow.'
      warningText='This action only returns selected orders to the PerfectRx workflow. It does not remove orders from the external compounding pharmacy system — the pharmacy may still have these orders.'
      noteLabel='Reason'
      noteHelperText="This reason is recorded in the order's history and audit trail. It is not sent to the external pharmacy."
      notePlaceholder='Reason for routing these orders to default…'
      noteAriaLabel='route to default reason'
      // Navy primary, not MUI 'warning' — the admin-portal theme renders
      // color='warning' as dark navy (dark-on-dark), so 'primary' stays readable.
      primaryColor='primary'
      getPrimaryLabel={() => 'Route to Default'}
      confirmTitle='Confirm Route to Default'
      getConfirmCountText={(count) =>
        `${count === 1 ? 'Order' : 'Orders'} will be returned to the PerfectRx workflow`
      }
      consequences={[
        'Returned to the appropriate PerfectRx workflow queue',
        'The external compounding pharmacy is NOT notified and may still have the order',
        'Recorded as a manual operational override',
      ]}
      notePreviewLabel='Reason'
      action={forceReturnCentralFillOrdersApi}
      getSuccessMessage={(successCount) =>
        `${successCount} ${successCount === 1 ? 'order' : 'orders'} routed to the default workflow.`
      }
      getPartialMessage={(successCount, failureCount) =>
        `${successCount} ${
          successCount === 1 ? 'order' : 'orders'
        } routed to the default workflow. ${failureCount} could not be routed — please retry those.`
      }
      getErrorMessage={() =>
        'Unable to route selected orders to default. Review details and try again.'
      }
    />
  );
};

ForceReturnCentralFillOrdersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string.isRequired,
      trackingId: PropTypes.string.isRequired,
      orderId: PropTypes.string,
      patientName: PropTypes.string,
      drugName: PropTypes.string,
      partnerName: PropTypes.string,
      compoundingPharmacyName: PropTypes.string,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onProcessed: PropTypes.func.isRequired,
};

export default ForceReturnCentralFillOrdersModal;
